import { useNavigate, useOutletContext } from 'react-router-dom';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import CostShareBar from '../../../components/AiSpend/CostShareBar';
import UsageCell from '../../../components/AiSpend/UsageCell';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import Pagination from '../../../components/Table/Pagination';
import { getAiSpendSessions } from '../../../services/aiSpendService';
import {
  formatCount,
  formatInstant,
  formatMoney,
} from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';
import { AI_SPEND_BASE } from './aiSpendPages';

const PROVIDER_COLORS = {
  openai: '#2C59F8',
  elevenlabs: '#8B5CF6',
  heygen: '#F45B2B',
  liveavatar: '#6DD24A',
};

export default function AiSpendSessions() {
  const navigate = useNavigate();
  const { filters, setFilters, queryParams, queryKey, options } = useOutletContext();
  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) =>
      getAiSpendSessions({
        params: { ...queryParams, page: filters.page, page_size: filters.page_size },
        signal,
      }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          Spend by interview session
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The real unit of work here: one interview draws on transcription, a judge
          model, speech synthesis and a live avatar. This is the only view that adds
          them up end to end.
        </p>
      </div>
      <AnalyticsTable
        loading={loading}
        rows={data?.results || []}
        rowKey={(row) => row.session}
        onRowClick={(row) =>
          navigate(`${AI_SPEND_BASE}/runs?session=${row.session}`)
        }
        emptyTitle="No session spend"
        columns={[
          { key: 'student_email', label: 'Student', render: (row) => row.student_email },
          { key: 'organization', label: 'Organization', render: (row) => row.organization },
          {
            key: 'started',
            label: 'Started',
            render: (row) => formatInstant(row.started, options?.timezone),
          },
          {
            key: 'cost_usd',
            label: 'Cost',
            render: (row) => formatMoney(row.cost_usd),
            className: 'font-semibold',
          },
          { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
          {
            key: 'usage',
            label: 'Usage',
            render: (row) => <UsageCell usage={row.usage} billingUnit={row.billing_unit} />,
          },
          {
            key: 'mix',
            label: 'Provider mix',
            render: (row) => (
              <CostShareBar
                segments={(row.cost_by_provider || []).map((provider) => ({
                  key: provider.key,
                  label: provider.label,
                  value: provider.cost_usd,
                  color: PROVIDER_COLORS[provider.key] || '#717C9E',
                }))}
              />
            ),
          },
        ]}
        footer={
          <Pagination
            page={filters.page}
            pageSize={filters.page_size}
            count={data?.count || 0}
            itemLabel="sessions"
            onPageChange={(page) => setFilters({ page })}
          />
        }
      />
    </div>
  );
}
